import { useState } from "react";

import { useTranslation } from "react-i18next";

import {
  CalendarClock,
  Check,
  MoreHorizontal,
  Pencil,
  Undo2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  useUpdatePlannedOrder,
  type PlannedOrder,
  type PlannedOrderStatus,
} from "@/api/planningRun";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CancelOrderDialog } from "./CancelOrderDialog";
import { LastsUntilDialog } from "./LastsUntilDialog";
import { OrderEditDialog } from "./OrderEditDialog";

/** The planner's decisions on an order. */
export function OrderActions({ order }: { order: PlannedOrder }) {
  const { t } = useTranslation();
  const update = useUpdatePlannedOrder();
  const [dialog, setDialog] = useState<
    "edit" | "lastsUntil" | "discard" | null
  >(null);
  const { status } = order;
  const openOrder = order.sourceExecution != null;

  const decide = (to: PlannedOrderStatus) =>
    update.mutate(
      { run: order.run, id: order.id, status: to },
      {
        onError: () =>
          toast.error(
            t(
              "planningReview.saveFailed",
              "Could not save this order. Reload the plan and try again.",
            ),
          ),
      },
    );

  return (
    <div className="flex items-center gap-1.5">
      {status === "planned" && (
        <Button disabled={update.isPending} onClick={() => decide("firmed")}>
          <Check />
          {t("planningReview.firm", "Accept")}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            disabled={update.isPending}
            aria-label={t("planningReview.orderActions", "Order actions")}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {status !== "cancelled" && (
            <DropdownMenuItem onSelect={() => setDialog("edit")}>
              <Pencil />
              {t("planningReview.edit", "Edit")}
            </DropdownMenuItem>
          )}
          {status !== "cancelled" && (
            <DropdownMenuItem onSelect={() => setDialog("lastsUntil")}>
              <CalendarClock />
              {t("planningReview.sizeToLast", "Size to last until")}
            </DropdownMenuItem>
          )}
          {(status === "planned" || (status === "firmed" && openOrder)) && (
            <DropdownMenuItem onSelect={() => setDialog("discard")}>
              <X />
              {t("planningReview.cancel", "Discard")}
            </DropdownMenuItem>
          )}
          {status === "firmed" && !openOrder && (
            <DropdownMenuItem onSelect={() => decide("planned")}>
              <Undo2 />
              {t("planningReview.undoFirm", "Undo acceptance")}
            </DropdownMenuItem>
          )}
          {status === "cancelled" && !openOrder && (
            <DropdownMenuItem onSelect={() => setDialog("discard")}>
              <Pencil />
              {t("planningReview.changeSkipDate", "Change skip date")}
            </DropdownMenuItem>
          )}
          {/* An open order is never undecided: undoing its discard firms it. */}
          {status === "cancelled" && (
            <DropdownMenuItem
              onSelect={() => decide(openOrder ? "firmed" : "planned")}
            >
              <Undo2 />
              {t("planningReview.undoCancel", "Undo discard")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog === "edit" && (
        <OrderEditDialog order={order} onClose={() => setDialog(null)} />
      )}
      {dialog === "lastsUntil" && (
        <LastsUntilDialog order={order} onClose={() => setDialog(null)} />
      )}
      {dialog === "discard" && (
        <CancelOrderDialog order={order} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
